// @ts-nocheck
// since still have to do "custom" add-sku, will wait to type this file.
import get from 'lodash/get';
import merge from 'lodash/merge';

// import { partnerCoBrandExistsAllAvenuesSelector } from 'src/selectors/partners';
import { isValidNonEmptyArray } from '@utils/array';
import { isPlainObjectWithKeys } from '@utils/object';
import { REMOTE_TRUE, REMOTE } from './addsku.constants';

/* Types */
import { Answer, Question, SelectedSkuQuestions, Sku } from 'types/sku.types';
import { Cart } from 'types/cart.types';
import { AddSkuStatePayload } from '@scenes/AddSkuPage/context/addsku-state.types';
import { QuestionTypes } from 'types/qa.types';

type FilterRemoteQuestionsProps = {
  sku: Sku;
  item: AddSkuStatePayload;
  questions: Question[];
  cart: Cart;
};

type CustomOpts = {
  showDialog: boolean;
  title: string;
  description: string;
};

export function checkPartnerCart(cart: Cart, sku: Sku) {
  const defaultOpts = {
    showDialog: false,
    title: '',
    description: '',
  };

  if (cart?.items?.length === 0) return defaultOpts;
  if (cart?.partner?.id === sku?.partner?.id) return defaultOpts;

  const customOpts = <CustomOpts>{};
  customOpts.showDialog = true;
  customOpts.title = `Adding this service will remove the ${!cart.partner && sku.partner ? 'non-' : ''}partner service(s) already in your cart.`;
  customOpts.description = `If you need additional ${
    sku.partner ? '' : 'non-'
  }partner service(s), go directly to the cart to complete booking and then proceed with requesting the additional service.`;

  return merge({}, defaultOpts, customOpts);
}

export function hasQuestionAnswers(question: SelectedSkuQuestions) {
  if (question) {
    if (question.id && question.id !== REMOTE_TRUE) {
      return true;
    }
    if (isPlainObjectWithKeys(question) && question.id !== REMOTE_TRUE && question.text !== '') {
      return true;
    }
  }
  return false;
}

export function filterRemoteQuestions({ sku, item, questions, cart }: FilterRemoteQuestionsProps) {
  let filteredQuestions = questions;

  if (sku.remote) {
    const itemQuestions = item.questions;
    const forbidRemote =
      isPlainObjectWithKeys(itemQuestions) &&
      !!questions.find(question => {
        const itemQuestion = itemQuestions?.[question.id.toString()];

        if (hasQuestionAnswers(itemQuestion)) {
          return question.answers.find((answer: Answer) => {
            if (answer.forbidRemote) {
              if (itemQuestion && itemQuestion.id && answer.id === itemQuestion.id) {
                return true;
              }
              if (itemQuestion && Array.isArray(itemQuestion)) {
                return itemQuestion.find(a => a.id === answer.id);
              }
            }
            return false;
          });
        }
        return false;
      });
    if (forbidRemote || !cart.remotePossible) {
      filteredQuestions = filteredQuestions.filter(q => q.id !== REMOTE);
    }
  }
  return filteredQuestions;
}

export function getItemDisplayPrice({ item, total = '' }: { item: AddSkuStatePayload; total: string | number }) {
  // - If the item does not have a 'startsAtPrice' return `totalPrice` by default on page load.
  // - If the item has a 'startsAtPrice' return that by default on page load.
  // - If the item has a 'startsAtPrice' and the customer makes a price adjustment, then return the total price. Per Minah (GH Jun 20, 2018)
  const basePriceOverridden = item?.overrideBasePrice;
  const startsAtPrice = item?.startsAtPrice;
  const totalPriceWithoutChanges = startsAtPrice;
  const totalPriceWithChanges = total;
  let displayPrice = '';

  if (!basePriceOverridden) {
    return totalPriceWithChanges;
  }
  if (basePriceOverridden) {
    if (totalPriceWithoutChanges === totalPriceWithChanges) {
      displayPrice = startsAtPrice;
    } else {
      displayPrice = totalPriceWithChanges;
    }
  }
  return displayPrice;
}

/*
 * Does our particular add-sku form have errors?
 * */
export const getErrors = ({ item, questions }) => {
  const requiredQuestions = questions.filter(q => q.required);
  const errors = {};

  requiredQuestions.forEach(q => {
    const answer = get(item, `questions.${q.id}`, '');
    let error = null;

    switch (q.inputType) {
      case QuestionTypes.Input:
        if (answer.text.trim() === '') {
          error = 'This field is required';
        }
        break;
      case QuestionTypes.Textarea:
        if (answer.text.trim() === '') {
          error = 'This field is required';
        }
        break;
      case QuestionTypes.Dropdown:
        if (!answer) {
          error = 'This field is required';
        }
        break;
      case QuestionTypes.Checkbox:
        if (!isValidNonEmptyArray(answer)) {
          error = 'Please select at least one option';
        }
        break;
      case QuestionTypes.Device: {
        const required = ['make', 'model'];
        const deviceErrors = required.reduce((seed, key) => {
          const keyRef = get(answer, key) ?? '';
          const keyRefValue = typeof keyRef === 'string' ? keyRef.trim() : keyRef;
          if (!keyRefValue) seed[key] = 'This field is required';

          return seed;
        }, {} as IHash<string>);

        error = Object.values(deviceErrors).length ? deviceErrors : null;
        break;
      }
      default:
        throw new Error(`Unknown input type: ${q.inputType}`);
    }
    if (error) {
      errors[q.id] = error;
    }
  });

  return { errors, hasErrors: Boolean(Object.values(errors).length) };
};
